import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import React, { Fragment, useEffect, useState } from 'react';

import { Grid, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import {
    Menu,
    Typography,
    SnackBarAlert,
    CircleLoading,
    TablePagination,
    DataTableWithAccordion,
} from 'shared';
import HeaderInfoModal from './HeaderInfoModal';
import { snackAlertData } from 'utils/constants';
import EditPurchaseModal from './EditPurchaseModal';
import SplitPurchaseModal from './SplitPurchaseModal';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import PurchaseActivityFilters from './PurchaseActivityFilters';
import { useInventoryService } from 'store/services/inventory.service';
import { purchaseActivityTableHeaders } from 'components/Inventory/utils/purchaseActivityTableHeaders';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;

const PurchaseActivity = ({}) => {
    const {
        splitPayment,
        getCompanyPosStatus,
        updateAggFileInventory,
        unmatchPurchaseTransaction,
        getAggregatedFileInventories,
    } = useInventoryService();
    const companyData = useSelector((state) => state.company.data);

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState([]);
    const [syncStatus, setSyncStatus] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [filterValues, setFilterValues] = useState({
        tags: [],
    });
    const [tableLoading, setTableLoading] = useState(true);
    const [currentHeader, setCurrentHeader] = useState(null);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedRow, setMenuSelectedRow] = useState(null);
    const [splitModalOpen, setSplitModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const handleSetAnchorEl = (event, row) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedRow(row);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const onEditCallback = async (data) => {
        try {
            await updateAggFileInventory(data);
            handleSuccessAlert(setAlertData, 'Purchase updated successfully!');
            handleGetInitialData();
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleUnmatchPurchaseTransaction = async (purchase) => {
        try {
            const response = await unmatchPurchaseTransaction({
                company_id: companyData.id,
                plaid_transaction_id: purchase.matched_plaid_transaction,
                aggregated_file_inventory_id: purchase.id,
                payment_plan_id: purchase.matched_payment_plan,
            });
            handleSuccessAlert(setAlertData, response.message);
            const pageNumber = data?.results?.length == 1 && page != 1 ? page - 1 : page;
            setPage(pageNumber);
            await handleGetAggregatedFileInventories(pageNumber, filterValues);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetAggregatedFileInventories = async (page, filters = {}) => {
        try {
            setTableLoading(true);
            const response = await getAggregatedFileInventories({
                companyId: companyData.id,
                page,
                isReviewed: 'all',
                ...filters,
                tag_ids: filters.tags ? filters.tags.map((tag) => tag.id || tag) : undefined,
            });
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handlePageChange = async (page) => {
        setPage(page);
        setSelected([]);
        await handleGetAggregatedFileInventories(page, filterValues);
    };

    const downloadPurchaseActivityCsv = (data) => {
        const headers = purchaseActivityTableHeaders(null, () => {});

        const csvHeaders = headers.map((col) => col.label);
        const csvRows = [csvHeaders.join(',')];

        data.forEach((item) => {
            const rowValues = headers.map((col) => {
                switch (col.id) {
                    case 'description':
                        return `${item.description}, ${item.expires_at}, Section: ${item.class_1}, Row: ${item.class_2}, Seats: ${item.seats}`;

                    case 'plaidTransactionStatus':
                        return item.is_reviewed ? 'Matched' : 'Not matched';

                    case 'amount':
                        return item.purchase_price;

                    case 'matchedBy':
                        return item.first_name ? `${item.first_name} ${item.last_name}` : '';

                    default:
                        return item[col.id] ?? '';
                }
            });

            const csvLine = rowValues.map((val) => `"${val}"`).join(',');

            csvRows.push(csvLine);
        });

        const csvString = csvRows.join('\n');

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `purcahse_activity_${new Date()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadPurchaseActivity = async () => {
        try {
            setDownloadLoading(true);
            const response = await getAggregatedFileInventories({
                companyId: companyData.id,
                isReviewed: 'all',
                ...filterValues,
            });
            downloadPurchaseActivityCsv(response.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleGetInitialData = async () => {
        try {
            setPage(1);
            setSelected([]);
            setFilterValues({});
            handleGetAggregatedFileInventories(1);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleFiltersSubmit = async (values) => {
        try {
            setPage(1);
            setSelected([]);
            const newFilterValues = { ...filterValues, ...values };
            setFilterValues(newFilterValues);
            handleGetAggregatedFileInventories(1, newFilterValues);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleApplyTags = (selectedTags) => {
        handleFiltersSubmit({ ...filterValues, tags: selectedTags });
    };

    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            const selectedRows = [];
            data?.results?.forEach((cellData) => {
                if (!selected.includes(cellData.id)) {
                    selectedRows.push(cellData.id);
                }
            });
            setSelected([...selected, ...selectedRows]);
        } else {
            setSelected([]);
        }
    };

    const handleSelectRow = (event, id) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter((item) => item !== id));
        }
    };

    const handleGetCompanyPosStatus = async () => {
        try {
            const response = await getCompanyPosStatus(companyData?.id);
            setSyncStatus(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const onSplitCallback = async (data) => {
        try {
            await splitPayment(data);
            handleSuccessAlert(setAlertData, 'Purchase split successfully!');
            handleGetInitialData();
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    useEffect(() => {
        if (companyData) {
            handleGetAggregatedFileInventories(page);
            handleGetCompanyPosStatus();
        }
    }, [companyData]);

    useEffect(() => {
        if (!companyData) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `inventory-purchase-sync/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            handleSuccessAlert(setAlertData, `Purchase Data processed successfully`);

            setSyncStatus((prev) => ({
                ...prev,
                purchase_status: data.status,
            }));
            handleGetInitialData();
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData]);

    return (
        <Fragment>
            {companyData?.id && data ? (
                <Grid container>
                    <Grid item xs={12}>
                        <PurchaseActivityFilters
                            data={data}
                            selected={selected}
                            syncStatus={syncStatus}
                            setSelected={setSelected}
                            setAlertData={setAlertData}
                            setSyncStatus={setSyncStatus}
                            downloadLoading={downloadLoading}
                            handleFiltersSubmit={handleFiltersSubmit}
                            handleGetInitialData={handleGetInitialData}
                            handleDownloadPurchaseActivity={handleDownloadPurchaseActivity}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DataTableWithAccordion
                            data={data}
                            selected={selected}
                            isSelectable={true}
                            isLoading={tableLoading}
                            setSelected={setSelected}
                            showHeaderCheckBox={true}
                            handleSelectRow={handleSelectRow}
                            handleSelectAllRows={handleSelectAllRows}
                            headers={purchaseActivityTableHeaders(
                                (header) => {
                                    setCurrentHeader(header);
                                    setModalOpen(true);
                                },
                                syncStatus,
                                handleSetAnchorEl,
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} p={'10px 0'}>
                        <TablePagination
                            page={page}
                            count={data?.count}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                        />
                    </Grid>
                </Grid>
            ) : (
                <CircleLoading />
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />

            <Menu
                items={[
                    {
                        name: (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                                <Typography text="Edit" />
                            </Box>
                        ),
                        onClick: () => {
                            if (menuSelectedRow?.is_reviewed) {
                                handleErrorAlert(
                                    setAlertData,
                                    'You need to unmatch before you edit or split purchase.',
                                );
                                handleCloseMenu();
                                return;
                            }
                            handleCloseMenu();
                            setEditModalOpen(true);
                        },
                    },
                    {
                        name: (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CallSplitIcon sx={{ mr: 1, fontSize: 20 }} />
                                <Typography text="Split Payment" />
                            </Box>
                        ),
                        onClick: () => {
                            if (menuSelectedRow?.is_reviewed) {
                                handleErrorAlert(
                                    setAlertData,
                                    'You need to unmatch before you edit or split purchase.',
                                );
                                handleCloseMenu();
                                return;
                            }
                            handleCloseMenu();
                            setSplitModalOpen(true);
                        },
                    },
                    ...(menuSelectedRow?.is_reviewed
                        ? [
                              {
                                  name: (
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <LinkOffIcon sx={{ mr: 1, fontSize: 20 }} />
                                          <Typography text="Unmatch Payment" />
                                      </Box>
                                  ),
                                  onClick: () => {
                                      handleUnmatchPurchaseTransaction(menuSelectedRow);
                                      handleCloseMenu();
                                  },
                              },
                          ]
                        : []),
                ]}
                anchorEl={anchorEl}
                handleClose={handleCloseMenu}
            />

            <SplitPurchaseModal
                open={splitModalOpen}
                onClose={() => setSplitModalOpen(false)}
                data={menuSelectedRow}
                onSplitCallback={onSplitCallback}
            />

            <EditPurchaseModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                data={menuSelectedRow}
                onEditCallback={onEditCallback}
            />

            <HeaderInfoModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onApplyTags={handleApplyTags}
                header={currentHeader}
                companyId={companyData?.id}
                selectedTagIds={filterValues.tags || []}
            />
        </Fragment>
    );
};

export default PurchaseActivity;
